import CloseIcon from '@mui/icons-material/Close';
import { FileUploadRounded } from '@mui/icons-material';
import { gray } from '../../../theme/themePrimitives';
import styled from 'styled-components';
import { Box, IconButton, Typography } from '@mui/material';
import { useRef, useState } from 'react';

const StyledInputBox = styled(Box)`
  height: 140px;
  width: 100%;
  border-radius: 6px;
  border: 1px dashed rgba(0, 0, 0, 0.24);
  background-color: #f8f9fb;
  position: relative;
  overflow: hidden;
  &:hover {
    cursor: pointer;
  }
`;

const StyledFileInput = styled('input')`
  display: none;
`;

const StyledTitle = styled(Typography)`
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  line-height: 0.9rem !important;
  text-align: center;
  color: #475464;
`;

const ImagePreview = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
  object-position: left;
`;

const CloseButton = styled(IconButton)`
  background-color: ${gray[200]} !important;
  position: absolute;
  top: -50px;
  left: -225px;
  z-index: 1000;
  &:hover {
    background-color:${gray[400]} !important;
  }
`;

interface ImageInputProps {
    title?: string;
    value?: string | null;
    onChange: (file: string | null) => void;
    icon?: React.ReactNode;
    disabled: boolean;
}

const ImageInput = ({ title, value, onChange, disabled }: ImageInputProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(value || null);
  const [, setError] = useState<string>('');
  let lastClickTime = 0;

  const validateFileType = (file: File) => {
    const validImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/jpg'
    ];
    return validImageTypes.includes(file.type);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const file = event.target.files?.[0];
    if (file) {
      if (validateFileType(file)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          setSelectedFile(reader.result as string);
          onChange(reader.result as string);
        };
        // const fileUrl = URL.createObjectURL(file);
        // onChange(file);
        // setError(""); // Clear error if valid
      } else {
        setError('Please upload a valid image file (jpg, png, gif).');
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    const now = Date.now();
    if (now - lastClickTime < 500) return;
    lastClickTime = now;

    if (fileInputRef.current) {
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  }
    // fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      if (validateFileType(file)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          setSelectedFile(reader.result as string);
          onChange(reader.result as string);
        };
        // const fileUrl = URL.createObjectURL(file);
        // setSelectedFile(fileUrl);
        // onChange(file);
        // setError(""); // Clear error if valid
      } else {
        setError('Please upload a valid image file (jpg, png, gif).');
      }
    }
  };

  const handleRemoveImage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedFile(null);
    onChange(null);
    setError('');
  };

  return (
    <StyledInputBox display="flex" alignItems="center" justifyContent="center" onClick={handleClick}>
      {selectedFile
      ? (
        <Box onClick={handleClick}>
          <StyledFileInput
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            disabled={disabled}
          />
          <ImagePreview src={selectedFile} alt="preview" />
          <CloseButton onClick={handleRemoveImage} size="small">
            <CloseIcon fontSize="small" />
          </CloseButton>
        </Box>
      )
      : (
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <StyledFileInput
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Box bgcolor={gray[200]} p={1} display="flex" justifyContent="center" alignItems="center" borderRadius={1} color={gray[500]}>
              <FileUploadRounded />
            </Box>
            {title && <StyledTitle>{title}</StyledTitle>}
            <StyledTitle
              sx={{
                fontWeight: '400 !important',
                marginTop: '5px'
              }}
            >
              Drop your Image here, or <br />
              <Typography component="span" variant="body2" fontWeight="bold" textAlign="center" sx={{ color: '#315E9C' }}>
                Click to Upload
              </Typography>
            </StyledTitle>
          </Box>
        </Box>
      )}
    </StyledInputBox>
  );
};

export default ImageInput;
